import React, { useState } from 'react';
import {
  Box,
  Chip,
  Typography,
  Button,
  Stack,
  Modal,
  Paper,
} from '@mui/material';
import COLORS from '../Utils/colors';

interface Category {
  name: string;
  priority?: number;
  featured?: boolean;
}

interface TripCategoryFilterProps {
  categories: Category[];
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  showViewAll?: boolean;
}

const TripCategoryFilter: React.FC<TripCategoryFilterProps> = ({
  categories,
  selectedCategory,
  onCategorySelect,
  showViewAll = true,
}) => {
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Add "All" option at the beginning
  const allCategories = [
    { name: 'All' },
    ...categories
  ];

  // Show first 6 categories by default (including "All"), or all if showAllCategories is true
  const displayCategories = showAllCategories ? allCategories : allCategories.slice(0, 6);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1.5
      }}>
        <Typography variant="subtitle1" fontWeight="600">
          Categories
        </Typography>

        {showViewAll && categories.length > 3 && (
          <Button
            variant="text"
            size="small"
            sx={{
              color: 'primary.main',
              textTransform: 'none',
              fontWeight: 500,
              p: 0,
              minWidth: 'auto',
            }}
            onClick={() => setModalOpen(true)}
          >
            View All
          </Button>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 1,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari and Opera
          },
        }}
      >
        {displayCategories.map((category) => (
          <Chip
            key={category.name}
            label={category.name}
            onClick={() => onCategorySelect(category.name)}
            variant={selectedCategory === category.name ? 'filled' : 'outlined'}
            sx={{
              borderRadius: 3,
              minWidth: 'fit-content',
              fontWeight: 500,
              px: 1,
              backgroundColor: selectedCategory === category.name
                ? '#FEB380'
                : 'transparent',
              '&:hover': {
                backgroundColor: selectedCategory === category.name
                  ? '#FEB380'
                  : 'action.hover'
              },
            }}
          />
        ))}
      </Stack>

      {/* Modal for viewing all categories */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <Paper sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '400px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '80vh',
          overflow: 'auto',
          borderRadius: '24px',
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            All Categories
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            mt: 2,
            '& .MuiChip-root': {
              flex: '1 1 auto',
              minWidth: 'fit-content'
            }
          }}>
            {allCategories.map((category) => (
              <Chip
                key={category.name}
                label={category.name}
                onClick={() => {
                  onCategorySelect(category.name);
                  setModalOpen(false);
                }}
                variant={selectedCategory === category.name ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: 3,
                  fontWeight: 500,
                  px: 1,
                  backgroundColor: selectedCategory === category.name
                    ? '#FEB380'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: selectedCategory === category.name
                      ? '#FEB380'
                      : 'action.hover'
                  },
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant='contained' 
              sx={{borderRadius: "24px", px: 3, bgcolor: COLORS.primary}} 
              onClick={() => setModalOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default TripCategoryFilter; 